'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CalculatorResult {
  conflictCost: number;
  parkPingCost: number;
  savings: number;
  roi: number;
}

export default function ROICalculator() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [vehicles, setVehicles] = useState(2);
  const [conflicts, setConflicts] = useState(3);
  const [result, setResult] = useState<CalculatorResult | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (isHydrated) {
      calculateROI();
    }
  }, [vehicles, conflicts, isHydrated]);

  const calculateROI = () => {
    const avgConflictCost = 2500;
    const avgTimeLoss = 1500;
    const parkPingCardCost = 499;
    const parkPingAnnualCost = vehicles * parkPingCardCost;
    const annualConflictCost = conflicts * 12 * (avgConflictCost + avgTimeLoss);
    const savings = annualConflictCost - parkPingAnnualCost;
    const roi = savings > 0 ? ((savings / parkPingAnnualCost) * 100).toFixed(0) : 0;

    setResult({
      conflictCost: annualConflictCost,
      parkPingCost: parkPingAnnualCost,
      savings: savings,
      roi: parseFloat(roi as any),
    });
  };

  if (!isHydrated) {
    return (
      <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-full"></div>
          <div className="h-32 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
      <div className="flex items-center space-x-3 mb-6">
        <div className="bg-[#42AF47]/10 p-3 rounded-lg">
          <Icon name="CalculatorIcon" size={24} className="text-[#42AF47]" />
        </div>
        <div>
          <h3 className="text-2xl font-heading font-bold text-gray-900">
            ROI Calculator
          </h3>
          <p className="text-sm text-gray-600">
            Calculate your annual savings with ParkPing
          </p>
        </div>
      </div>

      <div className="space-y-8 mb-10">
        <div>
          <label className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">
              Number of Vehicles
            </span>
            <span className="text-lg font-heading font-bold text-[#42AF47]">
              {vehicles}
            </span>
          </label>
          <input
            type="range"
            min="1"
            max="10"
            value={vehicles}
            onChange={(e) => setVehicles(parseInt(e.target.value))}
            className="w-full h-2.5 bg-gray-200 rounded-full appearance-none cursor-pointer accent-[#42AF47]"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-2">
            <span>1</span>
            <span>10</span>
          </div>
        </div>

        <div>
          <label className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">
              Parking Conflicts per Month
            </span>
            <span className="text-lg font-heading font-bold text-[#42AF47]">
              {conflicts}
            </span>
          </label>
          <input
            type="range"
            min="0"
            max="10"
            value={conflicts}
            onChange={(e) => setConflicts(parseInt(e.target.value))}
            className="w-full h-2.5 bg-gray-200 rounded-full appearance-none cursor-pointer accent-[#42AF47]"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-2">
            <span>0</span>
            <span>10+</span>
          </div>
        </div>
      </div>

      {result && (
        <div className="space-y-6">
          <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-600 mb-1">Annual Conflict Cost</p>
                <p className="text-3xl font-heading font-bold text-red-600">
                  ₹{result.conflictCost.toLocaleString('en-IN')}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">ParkPing Investment</p>
                <p className="text-3xl font-heading font-bold text-[#42AF47]">
                  ₹{result.parkPingCost.toLocaleString('en-IN')}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[#42AF47]/5 rounded-xl p-6 border border-[#42AF47]/20">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <p className="text-sm text-gray-600 mb-1">Your Annual Savings</p>
                <p className="text-4xl md:text-5xl font-heading font-bold text-[#42AF47]">
                  ₹{result.savings.toLocaleString('en-IN')}
                </p>
              </div>
              <div className="text-center md:text-right">
                <p className="text-sm text-gray-600 mb-1">ROI</p>
                <p className="text-4xl md:text-5xl font-heading font-bold text-[#42AF47]">
                  {result.roi}%
                </p>
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center italic">
            *Estimated based on common parking-related delays, security involvement, and resident inconvenience in housing societies.
          </p>
        </div>
      )}
    </div>
  );
}